import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { invitesApi } from '../services/api';
import { CreateInviteData, CreateInviteResponse, InviteRole, TenantInvite } from '../types';

const ROLE_OPTIONS: { value: InviteRole; label: string; description: string }[] = [
  {
    value: 'student',
    label: 'Student',
    description: 'View moves, upload demonstrations, request feedback.',
  },
  {
    value: 'instructor',
    label: 'Instructor',
    description: 'Create moves and patterns, give feedback to students.',
  },
  {
    value: 'owner',
    label: 'Owner',
    description: 'Full admin access — manage content, users, and other admins.',
  },
];

type StatusFilter = 'pending' | 'all';

const InvitesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [lastCreated, setLastCreated] = useState<CreateInviteResponse | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: invites, isLoading, error } = useQuery({
    queryKey: ['invites', statusFilter],
    queryFn: () => invitesApi.getAll(statusFilter),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateInviteData>({ defaultValues: { role: 'student', ttlDays: 7 } });

  const createMutation = useMutation({
    mutationFn: invitesApi.create,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      setLastCreated(res);
      reset({ role: 'student', ttlDays: 7 });
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: invitesApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  });

  const onSubmit = (data: CreateInviteData) => {
    createMutation.mutate({
      email: data.email.trim().toLowerCase(),
      role: data.role,
      ttlDays: data.ttlDays ? Number(data.ttlDays) : undefined,
    });
  };

  const buildRedeemUrl = (code: string): string => {
    const { protocol, hostname, port } = window.location;
    const portPart = port ? `:${port}` : '';
    return `${protocol}//${hostname}${portPart}/redeem-invite?code=${encodeURIComponent(code)}`;
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((cur) => (cur === key ? null : cur)), 1500);
    } catch {
      window.prompt('Copy this URL:', text);
    }
  };

  const handleRevoke = (invite: TenantInvite) => {
    if (window.confirm(`Revoke invite for ${invite.email}?`)) {
      deleteMutation.mutate(invite.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    reset({ role: 'student', ttlDays: 7 });
  };

  const formatExpires = (iso: string | null): string => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    const now = Date.now();
    const days = Math.round((d.getTime() - now) / (1000 * 60 * 60 * 24));
    return `${d.toLocaleDateString()} (${days >= 0 ? `in ${days}d` : `${-days}d ago`})`;
  };

  type InviteStatus = 'redeemed' | 'expired' | 'pending';
  const computeStatus = (invite: TenantInvite): InviteStatus => {
    if (invite.redeemedAt) return 'redeemed';
    if (invite.expiresAt && new Date(invite.expiresAt).getTime() < Date.now()) return 'expired';
    return 'pending';
  };

  const statusStyle = (s: InviteStatus): React.CSSProperties => {
    switch (s) {
      case 'redeemed':
        return { color: '#28a745', fontWeight: 600 };
      case 'expired':
        return { color: '#888' };
      default:
        return { color: '#0066cc', fontWeight: 600 };
    }
  };

  return (
    <div className="invites-management">
      <div className="page-header">
        <h1>Invites Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setLastCreated(null);
            setShowForm(true);
          }}
          disabled={showForm}
        >
          Send Invite
        </button>
      </div>

      <p className="admin-page-intro">
        Invite users to this tenant. Each invite is bound to one email and grants the
        chosen role on redemption. Use these to onboard members of an{' '}
        <code>invite_only</code> tenant; they also work for <code>open</code> tenants
        when you want to seed an instructor or owner directly.
      </p>

      {showForm && (
        <div className="form-container">
          <h2>Send New Invite</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /.+@.+\..+/, message: 'Enter a valid email' },
                })}
                type="email"
                id="email"
                className={errors.email ? 'error' : ''}
                placeholder="someone@example.com"
              />
              {errors.email && <span className="error-text">{errors.email.message}</span>}
            </div>

            <div className="form-group">
              <label>Role *</label>
              <div className="role-option-list invite-role-options">
                {ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="role-option-card">
                    <input
                      {...register('role', { required: true })}
                      type="radio"
                      value={opt.value}
                      className="role-option-input"
                    />
                    <div>
                      <div className="role-option-title">{opt.label}</div>
                      <div className="role-option-desc">{opt.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ttlDays">Expires after (days)</label>
              <input
                {...register('ttlDays', {
                  min: { value: 1, message: 'Must be at least 1 day' },
                  max: { value: 90, message: 'Max 90 days' },
                  valueAsNumber: true,
                })}
                type="number"
                id="ttlDays"
                className={errors.ttlDays ? 'error' : ''}
              />
              {errors.ttlDays && <span className="error-text">{errors.ttlDays.message}</span>}
            </div>

            {createMutation.error && (
              <div className="error-text invite-form-error">
                {(createMutation.error as { response?: { data?: { error?: string } } })?.response
                  ?.data?.error || 'Failed to create invite'}
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Sending…' : 'Create Invite'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {lastCreated && (
        <div className="form-container invite-success-banner">
          <h2>Invite created — share this link</h2>
          <p className="invite-success-text">
            Sent to <strong>{lastCreated.email}</strong> as {lastCreated.role}. The link is
            single-use and expires{' '}
            {lastCreated.expiresAt ? formatExpires(lastCreated.expiresAt) : 'never'}.
          </p>
          <div className="invite-link-row">
            <input
              type="text"
              readOnly
              value={lastCreated.redeemUrl}
              className="invite-link-input"
              aria-label="Invite link"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() => copyToClipboard(lastCreated.redeemUrl, 'last-created')}
            >
              {copiedKey === 'last-created' ? 'Copied!' : 'Copy'}
            </button>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() => setLastCreated(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="table-controls invite-filter-controls">
        <button
          type="button"
          className={`btn btn-small ${statusFilter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setStatusFilter('pending')}
        >
          Pending
        </button>
        <button
          type="button"
          className={`btn btn-small ${statusFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setStatusFilter('all')}
        >
          All (history)
        </button>
      </div>

      {error ? (
        <div className="error-text">Failed to load invites.</div>
      ) : isLoading ? (
        <div className="loading">Loading invites…</div>
      ) : invites && invites.length > 0 ? (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => {
                const status = computeStatus(invite);
                return (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>
                      <span className="invite-role-cell">{invite.role}</span>
                    </td>
                    <td>
                      <span style={statusStyle(status)}>
                        {status === 'redeemed' && invite.redeemedAt
                          ? `Redeemed ${new Date(invite.redeemedAt).toLocaleDateString()}`
                          : status === 'expired'
                            ? 'Expired'
                            : 'Pending'}
                      </span>
                    </td>
                    <td>{formatExpires(invite.expiresAt)}</td>
                    <td>{new Date(invite.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        {status === 'pending' && (
                          <>
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() =>
                                copyToClipboard(buildRedeemUrl(invite.code), `row-${invite.id}`)
                              }
                            >
                              {copiedKey === `row-${invite.id}` ? 'Copied!' : 'Copy link'}
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleRevoke(invite)}
                              disabled={deleteMutation.isPending}
                            >
                              Revoke
                            </button>
                          </>
                        )}
                        {status !== 'pending' && (
                          <span className="invite-no-actions">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>
            {statusFilter === 'pending'
              ? 'No pending invites. Click "Send Invite" to create one.'
              : 'No invites yet. Click "Send Invite" to create the first one.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default InvitesManagement;
